import { slugify } from "@/shared/helpers/slug";
import type { AppBootContent } from "@/shared/settings";

/**
 * FONTE ÚNICA de rotas do storefront.
 *
 * Toda navegação interna deve nascer aqui — nenhum componente monta
 * template string de rota por conta própria. Os builders devolvem o formato
 * CANÔNICO de cada destino (o único que não custa redirect extra):
 *
 *  - categoria: /categoria/{slug-pai}/{slug}  (categoryPath)
 *  - produto:   /produto/{texto}-{id}         (productHref)
 *  - busca:     /busca?q=                     (searchHref)
 *  - info:      /info/{pageKey}               (infoPageHref)
 *  - receita:   /receitas/{slug}              (recipeHref)
 *  - página do portal (Páginas e Menus): /{slug}  (o href vem pronto da API)
 *  - Clube: /clube e /clube/{slug}                 (seção com layout próprio, ver modules/club)
 *
 * Links vindos do portal/legado passam por resolve-widget-link, que traduz
 * o formato gravado para estes builders.
 */
export const ROUTES = {
    home: "/",
    search: "/busca",
    category: "/categoria",
    product: "/produto",
    info: "/info",
    recipes: "/receitas",
    account: "/minha-conta",
    checkout: "/checkout",
    club: "/clube",
    error: "/erro",
} as const;

/**
 * Prefixos de rota REAIS do app — usados para validar caminhos vindos do
 * portal (redirect de categoria, deep links). Derivado de ROUTES: se a rota
 * não existe em src/app, ela não pertence a esta lista.
 */
export const STOREFRONT_PATH_PREFIXES: readonly string[] = Object.values(ROUTES).filter(
    (route) => route !== "/"
);

// ---------------------------------------------------------------------------
// Categoria
// ---------------------------------------------------------------------------

/**
 * Rota canônica (e amigável a SEO) da listagem de uma categoria: o caminho
 * hierárquico de slugs — /categoria/bebidas/bebidas-quentes. Recebe os NOMES
 * da trilha raiz → categoria; a página resolve andando a árvore por slug.
 */
export function categoryPath(trailNames: string[]): string {
    return `${ROUTES.category}/${trailNames.map(slugify).join("/")}`;
}

/**
 * Rota de categoria quando só há o id (links "categoria/{id}" do portal, sem
 * acesso à árvore). A página resolve pelo id e redireciona pro caminho
 * canônico de slugs — use categoryPath sempre que a trilha estiver à mão.
 */
export function categoryIdHref(id: number | string): string {
    return `${ROUTES.category}/${id}`;
}

// ---------------------------------------------------------------------------
// Produto
// ---------------------------------------------------------------------------

/** Slug canônico de produto: "{texto}-{id}" (mesmo formato do details.slug do backend). */
export function productSlug(name: string, productId: number): string {
    const text = slugify(name);
    return text ? `${text}-${productId}` : String(productId);
}

/**
 * Rota canônica da PDP. Prefira `slug` (details.slug pronto do backend);
 * sem ele, monta a partir de nome+id; só com o id, cai em /produto/{id}
 * (a PDP resolve pelo id e redireciona pro canônico).
 */
export function productHref(product: {
    slug?: string | null;
    name?: string | null;
    productId: number;
}): string {
    if (product.slug) return `${ROUTES.product}/${product.slug}`;
    if (product.name) return `${ROUTES.product}/${productSlug(product.name, product.productId)}`;
    return `${ROUTES.product}/${product.productId}`;
}

// ---------------------------------------------------------------------------
// Busca / institucional / conta / checkout
// ---------------------------------------------------------------------------

/** /busca?q={termo} — sem termo, a página de busca vazia. */
export function searchHref(term?: string | null): string {
    const trimmed = term?.trim();
    return trimmed ? `${ROUTES.search}?q=${encodeURIComponent(trimmed)}` : ROUTES.search;
}

/**
 * Produtos de uma marca: a busca pelo nome, filtrada pela marca. A busca não
 * aceita termo vazio, então o nome entra como `q` e o `brandIds` corta o resto.
 */
export function brandHref(brandId: number | string, brandName: string): string {
    const base = searchHref(brandName);
    return `${base}${base.includes("?") ? "&" : "?"}brandIds=${encodeURIComponent(String(brandId))}`;
}

/** Página criada no portal: um segmento só, que não é rota da loja (ver app/[slug]). */
export function isAppPagePath(pathname: string): boolean {
    return /^\/[^/]+$/.test(pathname) && !STOREFRONT_PATH_PREFIXES.includes(pathname);
}

/** A seção do Clube: o /clube e as páginas abaixo dele. */
export function isClubPath(pathname: string): boolean {
    return pathname === ROUTES.club || pathname.startsWith(`${ROUTES.club}/`);
}

/** Página institucional por pageKey (fallback: id numérico, que a rota também resolve). */
export function infoPageHref(pageKey: string | number): string {
    return `${ROUTES.info}/${pageKey}`;
}

/** Página da receita pelo slug gerado no portal a partir do título. */
export function recipeHref(slug: string): string {
    return `${ROUTES.recipes}/${slug}`;
}

export type AccountTab ="meus-pedidos" | "enderecos" | "favoritos";

/** /minha-conta, com aba opcional (?tab=). */
export function accountHref(tab?: AccountTab): string {
    return tab ? `${ROUTES.account}?tab=${tab}` : ROUTES.account;
}

/** Passos do checkout — ordem é informação (stepper navega por índice). */
export const CHECKOUT_ROUTES = {
    cart: ROUTES.checkout,
    delivery: `${ROUTES.checkout}/entrega`,
    /** Só entra no fluxo quando o frete escolhido exige agendamento. */
    schedule: `${ROUTES.checkout}/agendamento`,
    payment: `${ROUTES.checkout}/pagamento`,
    conclusion: `${ROUTES.checkout}/conclusao`,
} as const;

// ---------------------------------------------------------------------------
// Externo
// ---------------------------------------------------------------------------

/** true para URLs absolutas http(s) — tudo que não é rota interna do app. */
export function isExternalUrl(href: string | null | undefined): boolean {
    return Boolean(href && /^https?:\/\//i.test(href));
}

/**
 * Props de âncora para um href qualquer: externo abre em nova aba com
 * rel seguro; interno navega na própria aba. Espalhe no <a>/<Link> ou
 * repasse ao componente da lib.
 */
export function anchorTargetProps(href: string | null | undefined): {
    target: "_blank" | "_self";
    rel?: string;
} {
    return isExternalUrl(href)
        ? { target: "_blank", rel: "noopener noreferrer" }
        : { target: "_self" };
}

// ---------------------------------------------------------------------------
// first_page (app_boot)
// ---------------------------------------------------------------------------

/**
 * Mapa first_page (portal) → rota do app.
 * Conforme as telas forem criadas, adicione aqui: "clube": "/clube", etc.
 */
const FIRST_PAGE_ROUTES: Record<string, string> = {
    home: ROUTES.home,
};

const DEFAULT_FIRST_PAGE = ROUTES.home;

/**
 * Resolve a rota inicial a partir da regra de boot (app_boot.first_page).
 * Valores desconhecidos ou ausentes caem na home — o boot nunca quebra
 * por configuração errada no portal.
 */
export function resolveFirstPageRoute(boot: AppBootContent | null | undefined): string {
    const firstPage = boot?.first_page;

    return (firstPage && FIRST_PAGE_ROUTES[firstPage]) || DEFAULT_FIRST_PAGE;
}
